/**
 * ElevateToThreadModal — turn one plan check comment into a Thread, paired immediately to a
 * milestone as a passenger (docs/PASSENGERS.md).
 *
 * Most plan check comments are ordinary execution work — log it, respond, revise the sheet, done.
 * This exists for the minority that can't be closed by revising: they need an active chase (a new
 * consultant, a client decision, a fee negotiation). The milestone can't post while the thread is
 * open, so the chase stays visible instead of getting lost inside a checklist item.
 *
 * The milestone is picked here rather than inferred, because a review's comments don't always map
 * to one obvious milestone (a comment on an early round can still be open when the project has moved
 * to a later revision milestone) — same reasoning as LinkMilestoneModal in PlanCheckSections.jsx.
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { createThread, updateThread } from '../services/threads.js'
import { decideThread, getCandidateMilestones } from '../services/passengers.js'
import { getTeamMembers, contactIdForMember } from '../services/members.js'
import Select from './Select.jsx'

export default function ElevateToThreadModal({ projectId, initialTitle, initialDescription, onClose, onElevated }) {
  const [title,   setTitle]   = useState(initialTitle || '')
  const [desc,    setDesc]    = useState(initialDescription || '')
  const [courtMemberId, setCourtMemberId] = useState(ctx.currentMember?.id || '')
  const [teamMembers,   setTeamMembers]   = useState(null)
  const [milestoneId,   setMilestoneId]   = useState('')
  const [candidates,    setCandidates]    = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    getTeamMembers().then(setTeamMembers).catch(() => setTeamMembers([]))
    getCandidateMilestones(projectId)
      .then(res => setCandidates((res.candidates && res.candidates[projectId]) || []))
      .catch(() => setCandidates([]))
    return () => { document.body.style.overflow = '' }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const courtOptions = (teamMembers || []).map(m => ({ value: m.id, label: m.name }))
  const msOptions = (candidates || []).map(m => ({
    value: m.milestoneId,
    label: m.name + (m.inAir ? '  ·  in the air' : '') + (m.phaseName ? '  ·  ' + m.phaseName : ''),
  }))

  async function handleSubmit() {
    if (!title.trim() || !courtMemberId || !milestoneId || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const memberId   = ctx.currentMember?.id
      const memberName = ctx.currentMember?.fields?.['Name'] || ctx.currentMember?.fields?.['Member Name'] || null
      const thread = await createThread(projectId, title.trim(), 'Permitting Items', null, desc.trim() || undefined, memberId, {})
      if (thread.error) throw new Error(typeof thread.error === 'string' ? thread.error : 'Failed to create thread')
      const cid = await contactIdForMember(courtMemberId).catch(() => null)
      if (cid) await updateThread(thread.id, { 'Ball In Court': [cid] })
      const paired = await decideThread(memberId, memberName, thread.id, 'pair', { milestoneId })
      if (paired.error) throw new Error(typeof paired.error === 'string' ? paired.error : 'Thread created, but pairing to the milestone failed — pair it from Triage.')
      onElevated(thread.id)
    } catch (e) {
      setError(e.message || 'Failed to elevate to a thread')
      setSubmitting(false)
    }
  }

  const canSubmit = title.trim() && courtMemberId && milestoneId && !submitting

  const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
  const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="ElevateToThreadModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 440, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Elevate to Thread</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', marginBottom: 16, lineHeight: 1.4 }}>
          Only for comments that can't be closed by revising the drawings — this needs a chase, not a redline.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={labelStyle}>Title <span style={{ color: '#c0392b' }}>*</span></label>
            <input type="text" style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} autoFocus />
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 70 }} value={desc} onChange={e => setDesc(e.target.value)} placeholder="What needs to happen…" />
          </div>

          <div>
            <label style={labelStyle}>Whose court is it in? <span style={{ color: '#c0392b' }}>*</span></label>
            <Select
              value={courtMemberId} onChange={setCourtMemberId} options={courtOptions}
              placeholder={teamMembers === null ? 'Loading team…' : 'Pick a team member…'}
              style={{ width: '100%', fontSize: 12 }}
            />
          </div>

          <div>
            <label style={labelStyle}>Pair to milestone <span style={{ color: '#c0392b' }}>*</span></label>
            <Select
              value={milestoneId} onChange={setMilestoneId} options={msOptions}
              placeholder={candidates === null ? 'Loading milestones…' : (msOptions.length ? 'Pick a milestone…' : 'No open Active milestones on this project')}
              searchable
              style={{ width: '100%', fontSize: 12 }}
            />
            <div style={{ marginTop: 5, fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)' }}>
              That milestone can't post while this thread is open.
            </div>
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={onClose} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-mid)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default', transition: 'background 0.12s' }}
            >
              {submitting ? 'Elevating…' : 'Elevate to Thread'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
